import classNames from "classnames";
import flatten from "lodash/flatten";
import isNumber from "lodash/isNumber";
import range from "lodash/range";
import times from "lodash/times";
import { Moment, MomentInput } from "moment";
import { UNDETERMINED_FUTURE, UNDETERMINED_PAST } from "PFApp/constants/dates";
import { isActionKeyPressed } from "PFCore/helpers/is_action_key_pressed";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { WeekDay } from "PFTypes";
import { EventKey } from "PFTypes/event_key";
import {
  KeyboardEvent,
  PropsWithChildren,
  ReactElement,
  SyntheticEvent,
  useEffect,
  useRef,
  useState
} from "react";
import { useTranslation } from "react-i18next";

import { ISO_DATE_FORMAT, ISO_DATE_TIME_FORMAT, ISO_TIME_FORMAT, utc } from "../../helpers/date";
import { TimePicker } from "../time_picker";
import css from "./calendar.module.scss";
import CalendarDate from "./parts/calendar_date";
import { CalendarHeader } from "./parts/calendar_header";

const DEFAULT_HOUR = 12;
const DEFAULT_MINUTE = 0;

interface DateTime {
  date?: Moment;
  hour?: number;
  minute?: number;
}

export type CalendarProps = {
  displayMonthValue?: MomentInput;
  selectedDate?: string | null;
  label?: string;
  checkHighlighted?: (date: string) => boolean;
  checkHover?: (date: string) => boolean;
  checkSelected?: (date: string) => boolean;
  qaId?: string;
  minDate?: string;
  maxDate?: string;
  matchMaxDate?: string;
  showArrows?: boolean;
  showTime?: boolean;
  readOnly?: boolean;
  style?: React.CSSProperties;
  handleChange?: (date: string, options: { timeChanged?: boolean }, e: SyntheticEvent) => void;
  handleMouseLeave?: (date: string) => void;
  handleMouseEnter?: (date: string) => void;
  onKeyDown?: (event: KeyboardEvent) => void;
  footerMessage?: ReactElement<any> | string;
  sidePanel?: ReactElement<any>;
  portalRef?: React.RefObject<HTMLDivElement | null>;
  fixedWidth?: boolean;
  weekStartDay?: WeekDay;
};

const Calendar = ({
  displayMonthValue,
  selectedDate,
  maxDate = UNDETERMINED_FUTURE.toISOString(),
  minDate = UNDETERMINED_PAST.toISOString(),
  checkHighlighted,
  checkHover,
  checkSelected,
  readOnly,
  handleMouseEnter,
  handleMouseLeave,
  handleChange,
  onKeyDown,
  qaId = "Calendar",
  style,
  showArrows = true,
  label = "",
  matchMaxDate,
  showTime,
  sidePanel,
  footerMessage,
  children,
  portalRef,
  fixedWidth = true,
  weekStartDay = WeekDay.Monday
}: PropsWithChildren<CalendarProps>) => {
  const { t } = useTranslation("core", { keyPrefix: "components.calendar" });
  const calendarContainerRef = useRef<HTMLDivElement>(null);

  const { formatISODate, formatISOTime } = useDateFormatter();

  const [displayMonth, setDisplayMonth] = useState(utc(selectedDate || displayMonthValue || utc()));
  const [keyboardDate, setKeyboardDate] = useState<Moment | null>(null);

  useEffect(() => {
    if (utc(displayMonth).isBefore(utc(minDate), "month")) {
      setDisplayMonth(utc(minDate));
    } else if (utc(displayMonth).isAfter(utc(maxDate), "month")) {
      setDisplayMonth(utc(maxDate));
    }
  }, [minDate, maxDate]);

  const handleChangeTime = ({ date, hour, minute }: DateTime, event) => {
    let nextDate = selectedDate ? utc(selectedDate) : null;

    if (!nextDate) {
      nextDate = utc();

      if (formatISODate(nextDate) < formatISODate(minDate)) {
        nextDate = utc(minDate).clone();
      } else if (formatISODate(nextDate) > formatISODate(maxDate)) {
        nextDate = utc(maxDate).clone();
      }

      nextDate.set({ hour: DEFAULT_HOUR, minute: DEFAULT_MINUTE, second: 0 });
    }

    if (date) {
      nextDate.set({ year: date.year(), month: date.month(), date: date.date() });
    }

    if (isNumber(hour)) {
      nextDate.hour(hour).second(0);
    }
    if (isNumber(minute)) {
      nextDate.minute(minute).second(0);
    }

    handleChange?.(
      nextDate.format(showTime ? ISO_DATE_TIME_FORMAT : ISO_DATE_FORMAT),
      { timeChanged: true },
      event
    );
  };

  const getDayProps = (momentDate) => {
    const date = momentDate
      .clone()
      .date(momentDate.get("date"))
      .hour(DEFAULT_HOUR)
      .minute(DEFAULT_MINUTE)
      .second(0);

    const isSelected = (dateFormatted) => {
      const momentSelectedDate = utc(selectedDate || undefined);
      return !!(selectedDate && formatISODate(momentSelectedDate) === dateFormatted);
    };

    const dateFormat = formatISODate(date);
    const isMin = dateFormat === formatISODate(minDate);
    const isMax = dateFormat === formatISODate(maxDate);
    const isBeforeMin = isMin ? false : dateFormat < formatISODate(minDate);
    const isAfterMax = isMax ? false : dateFormat > formatISODate(maxDate);
    // We need to check if time will be within the range after changing to this date
    const isTimeInRange =
      !selectedDate ||
      ((isMax ? formatISOTime(maxDate) >= formatISOTime(selectedDate) : true) &&
        (isMin ? formatISOTime(minDate) <= formatISOTime(selectedDate) : true));

    const disabled = isBeforeMin || isAfterMax || !isTimeInRange;
    const selected = checkSelected ? checkSelected(date) : isSelected(dateFormat);
    const keyboardSelected = keyboardDate && formatISODate(keyboardDate) === dateFormat;
    const highlighted = checkHighlighted ? checkHighlighted(date) : false;
    const hovered = checkHover ? checkHover(date) : false;

    return {
      key: dateFormat,
      isCurrentMonth: displayMonth.get("month") === date.get("month"),
      date,
      selected,
      keyboardSelected,
      disabled,
      readOnly,
      highlighted,
      hovered,
      prevBusy: undefined,
      nextBusy: undefined,
      handleMouseEnter,
      handleMouseLeave,
      handleClick: (date, event) => handleChangeTime({ date }, event)
    };
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    let newKeyboardSelected;
    const currentKeyboardSelected = keyboardDate || displayMonth;

    if (
      [EventKey.ArrowLeft, EventKey.ArrowRight, EventKey.ArrowUp, EventKey.ArrowDown].includes(
        event.key as EventKey
      )
    ) {
      event.preventDefault();
    }

    if (event.key === EventKey.ArrowLeft) {
      newKeyboardSelected = currentKeyboardSelected.clone().subtract(1, "day");
    } else if (event.key === EventKey.ArrowRight) {
      newKeyboardSelected = currentKeyboardSelected.clone().add(1, "day");
    } else if (event.key === EventKey.ArrowUp) {
      newKeyboardSelected = currentKeyboardSelected.clone().subtract(1, "week");
    } else if (event.key === EventKey.ArrowDown) {
      newKeyboardSelected = currentKeyboardSelected.clone().add(1, "week");
    } else if (isActionKeyPressed(event) && keyboardDate) {
      handleChange?.(keyboardDate.format(ISO_DATE_FORMAT), { timeChanged: false }, event);
      return;
    }

    if (newKeyboardSelected) {
      const { disabled } = getDayProps(newKeyboardSelected);

      if (!disabled) {
        setKeyboardDate(newKeyboardSelected);
        setDisplayMonth(newKeyboardSelected);
      }
    }

    onKeyDown?.(event);
  };

  const indexOfStartDay = Object.values(WeekDay).indexOf(weekStartDay);
  const dateStart = displayMonth.clone().startOf("month");
  const dateEnd = displayMonth.clone().endOf("month");
  const prevCount = (dateStart.get("day") - indexOfStartDay - 1 + 7) % 7;
  const nextCount = 42 - prevCount - dateEnd.get("date");
  const isMinDate = minDate && formatISODate(utc(selectedDate)) === formatISODate(minDate);
  const isMaxDate = maxDate && formatISODate(utc(selectedDate)) === formatISODate(maxDate);
  const maxTime = isMaxDate ? formatISOTime(maxDate) : null;
  const minTime = isMinDate ? formatISOTime(minDate) : null;

  const daysProps = flatten([
    times(prevCount, (index) => getDayProps(dateStart.clone().add(index - prevCount, "days"))),
    range(1, dateEnd.get("date") + 1).map((index) => getDayProps(dateStart.clone().date(index))),
    times(nextCount, (index) => getDayProps(dateEnd.clone().add(index + 1, "days")))
  ]);

  const isBusy = (value) => {
    if (Array.isArray(value)) {
      return value.find(isBusy);
    }
    return value === true || value === "partial" || /Kind$/.test(value);
  };
  daysProps.forEach((props, i) => {
    props.prevBusy = isBusy(daysProps[i - 1]?.highlighted);
    props.nextBusy = isBusy(daysProps[i + 1]?.highlighted);
  });

  return (
    <div
      role="grid"
      tabIndex={-1}
      ref={calendarContainerRef}
      aria-label={t("chooseDate")}
      className={css.root}
      style={style}
      data-qa-id={qaId}
      onKeyDown={handleKeyDown}
    >
      <div className={classNames(css.calendar, { [css.calendarFixedWidth]: !!fixedWidth })} role="rowgroup">
        <CalendarHeader
          showArrows={showArrows !== undefined ? showArrows : true}
          label={label}
          minDate={minDate || UNDETERMINED_PAST.toISOString()}
          maxDate={maxDate || UNDETERMINED_FUTURE.toISOString()}
          displayMonth={displayMonth}
          setDisplayMonth={setDisplayMonth}
          weekStartDay={weekStartDay}
        />

        <div className={css.days} role="row">
          {daysProps.map((props) => (
            // @ts-ignore
            <CalendarDate {...props} matchMaxDate={matchMaxDate} key={props.key} />
          ))}
          {times(6, (number) => (
            <span key={number} className={css.dayBorder} style={{ left: `${(number + 1) * (100 / 7)}%` }} />
          ))}
        </div>

        {children}
        {showTime && (
          <TimePicker
            className={css.timePicker}
            max={maxTime}
            min={minTime}
            value={formatISOTime(selectedDate || utc().hours(DEFAULT_HOUR).minutes(DEFAULT_MINUTE))}
            onChange={(value) => {
              const timeMoment = utc(value, ISO_TIME_FORMAT);
              handleChangeTime({ hour: timeMoment.get("hour"), minute: timeMoment.get("minute") }, null);
            }}
            portalRef={portalRef}
          />
        )}

        {footerMessage}
      </div>
      {sidePanel}
    </div>
  );
};

export default Calendar;
